import React, { useEffect, useRef } from 'react';

import { FingerWritingAnswer } from '../../../models/GameStore';
import { HandWritingBlankUnit } from '../../Molecules';
import { HandwritingBlankInfo } from '../HandwritingGameMainGroup';

import styles from './HandwritingGameQuestionPanel.module.css';

export enum HandwritingGameMode {
  QuickHandwriting = 'QuickHandwriting',
}

type HandwritingGameQuestionPanelProps = {
  answer: FingerWritingAnswer;
  currentBlankIndex: number;
  blankInfos: Record<number, HandwritingBlankInfo>;
  mode: HandwritingGameMode;
  isLearner: boolean;
  isGameStart: boolean;
};

export const HandwritingGameQuestionPanel: React.FC<
  HandwritingGameQuestionPanelProps
> = ({ answer, currentBlankIndex, blankInfos, isLearner, isGameStart }) => {
  const blankRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const currentRef = blankRefs.current[currentBlankIndex];
    if (currentRef) {
      currentRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentBlankIndex]);

  // Precompute all blank indices for reference
  const allBlankIndices = answer.words.flatMap((word, wordIndex) =>
    word.characters
      .map((character, characterIndex) => ({
        fileId: character.fileId,
        strokeCount: character.strokeCount,
        wordIndex,
        characterIndex,
      }))
      .filter((item) => item.strokeCount),
  );

  // Render units and split into containers when encountering { type: 'hint', key: '\n' } or word: '\n'
  const renderAnswer = () => {
    const paragraphs: JSX.Element[] = [];
    let currentParagraph: JSX.Element[] = [];

    const makeParagraph = () => {
      if (currentParagraph.length > 0) {
        // Push the current container into the containers array
        paragraphs.push(
          <div
            key={paragraphs.length}
            className={`${styles.quickMemoryWordsContainer}`}
          >
            {currentParagraph}
          </div>,
        );

        // Reset current container
        currentParagraph = [];
      }
    };

    answer.words.forEach((word, wordIndex) => {
      if (word.word === '\n') {
        makeParagraph();

        paragraphs.push(
          <div
            key={`linebreak-${paragraphs.length}`}
            style={{
              height: '1rem', // Adjust line height as needed
              width: '100%',
              backgroundColor: 'transparent', // Transparent to avoid background conflict
            }}
          />,
        );
      } else {
        // Process units within the word
        word.characters.forEach((character, characterIndex) => {
          if (character.character === '\n') {
            makeParagraph();
          } else {
            // Add unit to the current container
            if (!character.strokeCount) {
              currentParagraph.push(
                <span
                  key={`${wordIndex}-${characterIndex}`}
                  className={styles.hint}
                >
                  {character.character}
                </span>,
              );
            } else if (character.strokeCount) {
              const globalBlankIndex = allBlankIndices.findIndex(
                (item) =>
                  item.wordIndex === wordIndex &&
                  item.characterIndex === characterIndex,
              );
              const isHighlighted = globalBlankIndex === currentBlankIndex;
              const isAnswered =
                blankInfos[globalBlankIndex]?.isAnswered ?? false;
              const score = blankInfos[globalBlankIndex]?.score ?? 100;

              currentParagraph.push(
                <HandWritingBlankUnit
                  key={`${wordIndex}-${characterIndex}`}
                  character={character}
                  isAnswered={isAnswered}
                  isHighlighted={isHighlighted}
                  score={score}
                  globalBlankIndex={globalBlankIndex}
                  isLearner={isLearner}
                  blankRefs={blankRefs}
                />,
              );
            }
          }
        });
      }
    });

    makeParagraph();

    return paragraphs;
  };

  return (
    <div
      className={`${styles.container} ${
        isLearner ? styles.learnerContainer : ''
      }  ${isGameStart ? styles.gameStartContainer : ''}`}
    >
      <div className={styles.center}>
        <div className={styles.contentGroup}>{renderAnswer()}</div>
      </div>
    </div>
  );
};
